import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Spinner,
} from '../../../shared/components/ui';
import { ConfirmDialog } from '../../../shared/components/Dialogs/ConfirmDialog';
import { estimatesApi } from '../../../shared/services/estimatesApi';
import { formatCurrency, formatUnitCost, formatSqFt, formatDate } from '../../../shared/utils/formatters';
import { useTenantPreferences } from '../../../shared/hooks/useTenantPreferences';

function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <svg
        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />
    </div>
  );
}

function SelectInput({ value, onChange, options, placeholder }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900"
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function EstimateStatusBadge({ status }) {
  if (status === 'complete') {
    return (
      <Badge variant="green" dot>
        Complete
      </Badge>
    );
  }
  return (
    <Badge variant="yellow" dot>
      Draft
    </Badge>
  );
}

function SortIcon({ active, direction }) {
  if (!active) {
    return <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">↕</span>;
  }
  return <span aria-hidden="true">{direction === 'asc' ? '↑' : '↓'}</span>;
}

// A sortable table header cell. `align` controls text/flex alignment so numeric
// columns stay right-aligned; `className` carries responsive visibility.
function SortHeader({ label, sortKey, sortBy, sortDir, onSort, align = 'left', className = '' }) {
  const active = sortBy === sortKey;
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  const justifyClass =
    align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
  return (
    <th
      className={`py-3 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm ${alignClass} ${className}`}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`w-full inline-flex items-center gap-1 ${justifyClass} hover:text-gray-700 dark:hover:text-gray-200 transition-colors ${
          active ? 'text-gray-900 dark:text-gray-100' : ''
        }`}
      >
        <span>{label}</span>
        <SortIcon active={active} direction={sortDir} />
      </button>
    </th>
  );
}

function EstimatesTable({ estimates, onRowClick, onDelete, isSubcontractor = false, sortBy, sortDir, onSort }) {
  if (estimates.length === 0) {
    return (
      <div className="text-center py-12">
        <svg
          className="w-12 h-12 mx-auto text-gray-300 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <p className="text-gray-500 dark:text-gray-400">No estimates found matching your filters.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-800">
            <SortHeader label="Project" sortKey="projectName" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            {!isSubcontractor && (
              <SortHeader label="Job Type" sortKey="jobType" sortBy={sortBy} sortDir={sortDir} onSort={onSort} className="hidden md:table-cell" />
            )}
            <SortHeader label="Location" sortKey="location" sortBy={sortBy} sortDir={sortDir} onSort={onSort} className="hidden md:table-cell" />
            <SortHeader label="Size" sortKey="squareFootage" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" className="hidden md:table-cell" />
            <SortHeader label="Total Cost" sortKey="totalCost" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
            <SortHeader label="Fee / SF" sortKey="feePerSf" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
            <SortHeader label="Status" sortKey="status" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="center" className="hidden md:table-cell" />
            <SortHeader label="Docs" sortKey="documentCount" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="center" className="hidden md:table-cell" />
            <SortHeader label="Date" sortKey="estimateDate" sortBy={sortBy} sortDir={sortDir} onSort={onSort} className="hidden md:table-cell" />
            <th className="text-center py-3 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm w-20">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {estimates.map((estimate) => (
            <tr
              key={estimate.estimateId}
              className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
            >
              <td
                className="py-4 px-4 align-top"
                onClick={() => onRowClick(estimate.estimateId)}
              >
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100 break-words">{estimate.projectName}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 break-words">{estimate.clientName}</p>
                  {/* Mobile-only inline meta — surfaces what the hidden columns would show. */}
                  <div className="md:hidden mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                    {estimate.location && <span>{estimate.location}</span>}
                    <EstimateStatusBadge status={estimate.status || 'complete'} />
                    <span>{formatDate(estimate.estimateDate)}</span>
                  </div>
                </div>
              </td>
              {!isSubcontractor && (
                <td
                  className="hidden md:table-cell py-4 px-4"
                  onClick={() => onRowClick(estimate.estimateId)}
                >
                  <Badge variant="blue" size="sm">
                    {estimate.jobType}
                  </Badge>
                </td>
              )}
              <td
                className="hidden md:table-cell py-4 px-4 text-gray-700 dark:text-gray-200"
                onClick={() => onRowClick(estimate.estimateId)}
              >
                {estimate.location}
              </td>
              <td
                className="hidden md:table-cell py-4 px-4 text-right text-gray-700 dark:text-gray-200"
                onClick={() => onRowClick(estimate.estimateId)}
              >
                {formatSqFt(estimate.squareFootage)}
              </td>
              <td
                className="py-4 px-4 text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap align-top"
                onClick={() => onRowClick(estimate.estimateId)}
              >
                {formatCurrency(estimate.totalCost)}
              </td>
              <td
                className="py-4 px-4 text-right text-gray-700 dark:text-gray-200 whitespace-nowrap align-top"
                onClick={() => onRowClick(estimate.estimateId)}
              >
                {formatUnitCost(estimate.feePerSf)}
              </td>
              <td
                className="hidden md:table-cell py-4 px-4 text-center"
                onClick={() => onRowClick(estimate.estimateId)}
              >
                <EstimateStatusBadge status={estimate.status || 'complete'} />
              </td>
              <td
                className="hidden md:table-cell py-4 px-4 text-center"
                onClick={() => onRowClick(estimate.estimateId)}
              >
                {estimate.documentCount > 0 ? (
                  <div className="flex items-center justify-center gap-1 text-sm text-gray-600 dark:text-gray-300">
                    <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>{estimate.documentCount}</span>
                  </div>
                ) : (
                  <span className="text-gray-300 text-sm">—</span>
                )}
              </td>
              <td
                className="hidden md:table-cell py-4 px-4 text-gray-500 dark:text-gray-400 text-sm whitespace-nowrap"
                onClick={() => onRowClick(estimate.estimateId)}
              >
                {formatDate(estimate.estimateDate)}
              </td>
              <td className="py-4 px-4 text-center align-top">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(estimate.estimateId);
                  }}
                  className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                  title="Delete estimate"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    pages.push(i);
  }

  return (
    <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-800 pt-4">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Previous
      </Button>
      <div className="flex gap-2">
        {pages.map((page) => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${page === currentPage
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
          >
            {page}
          </button>
        ))}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        Next
      </Button>
    </div>
  );
}

export function ManageEstimates() {
  const navigate = useNavigate();
  const tenantPrefs = useTenantPreferences();
  // Sub-trade tenants don't get meaningful Job Type data (everything they
  // do is in one anchor division), so we hide the column and the filter.
  const isSubcontractor = !!tenantPrefs.subcontractorAnchorDivision;
  const [estimates, setEstimates] = useState([]);
  const [jobTypes, setjobTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Stats
  const [draftCount, setDraftCount] = useState(0);
  const [completeCount, setCompleteCount] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [jobType, setjobType] = useState('');
  const [location, setLocation] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Sorting (server-side across all pages). Text columns default to ascending,
  // numeric/date columns to descending.
  const [sortBy, setSortBy] = useState('estimateDate');
  const [sortDir, setSortDir] = useState('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEstimates, setTotalEstimates] = useState(0);

  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [estimateToDelete, setEstimateToDelete] = useState(null);

  // Location options
  const locationOptions = [
    { value: 'Cincinnati', label: 'Cincinnati, OH' },
    { value: 'Columbus', label: 'Columbus, OH' },
    { value: 'Cleveland', label: 'Cleveland, OH' },
    { value: 'Indianapolis', label: 'Indianapolis, IN' },
    { value: 'Louisville', label: 'Louisville, KY' },
    { value: 'Lexington', label: 'Lexington, KY' },
  ];

  // Status options
  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'complete', label: 'Complete' },
  ];

  // Load estimates (also returns job types for filter dropdown)
  const loadEstimates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await estimatesApi.getEstimates({
        search,
        jobType,
        location,
        status: statusFilter,
        sortBy,
        sortDir,
        page: currentPage,
        limit: 10,
      });
      setEstimates(result.estimates);
      setTotalPages(result.totalPages);
      setTotalEstimates(result.total);

      // Update job types for filter dropdown (returned from API)
      if (result.jobTypes && result.jobTypes.length > 0) {
        setjobTypes(result.jobTypes.map((t) => ({ value: t, label: t })));
      }

      // Use status counts from API (reflects total results, not just current page)
      if (result.statusCounts) {
        setDraftCount(result.statusCounts.draft || 0);
        setCompleteCount(result.statusCounts.complete || 0);
      }
    } catch (err) {
      setError(err.message || 'Failed to load estimates');
    } finally {
      setIsLoading(false);
    }
  }, [search, jobType, location, statusFilter, sortBy, sortDir, currentPage]);

  useEffect(() => {
    loadEstimates();
  }, [loadEstimates]);

  // Reset to page 1 when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, jobType, location, statusFilter, sortBy, sortDir]);

  const handleRowClick = (estimateId) => {
    navigate(`/estimates/${estimateId}`);
  };

  // Toggle direction when re-clicking the active column; otherwise switch column
  // and pick a sensible default direction for that column's data type.
  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
      return;
    }
    const textColumns = ['projectName', 'jobType', 'location'];
    setSortBy(key);
    setSortDir(textColumns.includes(key) ? 'asc' : 'desc');
  };

  const handleClearFilters = () => {
    setSearch('');
    setjobType('');
    setLocation('');
    setStatusFilter('');
  };

  const handleDeleteClick = (estimateId) => {
    setEstimateToDelete(estimateId);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!estimateToDelete) return;

    try {
      await estimatesApi.deleteEstimate(estimateToDelete);
      setShowDeleteDialog(false);
      setEstimateToDelete(null);
      // Refresh list
      loadEstimates();
    } catch (err) {
      console.error('Failed to delete estimate:', err);
      setError('Failed to delete estimate. Please try again.');
    }
  };

  const hasFilters = search || jobType || location || statusFilter;

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Estimates</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Manage all your estimates - both historical and AI-generated.
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/estimates/upload')}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 4 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            Upload
          </Button>
          <Button onClick={() => navigate('/estimates/new')}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create New Estimate
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-yellow-100 dark:bg-yellow-900/40 rounded-full">
            <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
          </div>
          <div>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">Draft Estimates</p>
            <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-200">{draftCount}</p>
          </div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-green-100 dark:bg-green-900/40 rounded-full">
            <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div>
            <p className="text-sm text-green-700 dark:text-green-300">Complete Estimates</p>
            <p className="text-2xl font-bold text-green-900 dark:text-green-200">{completeCount}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className={`grid grid-cols-1 ${isSubcontractor ? 'md:grid-cols-4' : 'md:grid-cols-5'} gap-4`}>
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search projects or clients..."
            />
            {!isSubcontractor && (
              <SelectInput
                value={jobType}
                onChange={setjobType}
                options={jobTypes}
                placeholder="All Job Types"
              />
            )}
            <SelectInput
              value={location}
              onChange={setLocation}
              options={locationOptions}
              placeholder="All Locations"
            />
            <SelectInput
              value={statusFilter}
              onChange={setStatusFilter}
              options={statusOptions}
              placeholder="All Statuses"
            />
            {hasFilters && (
              <Button variant="ghost" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              {isLoading ? 'Loading...' : `${totalEstimates} Estimates`}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 mb-4">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : (
            <>
              <EstimatesTable
                estimates={estimates}
                onRowClick={handleRowClick}
                onDelete={handleDeleteClick}
                isSubcontractor={isSubcontractor}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Estimate"
        message="Are you sure you want to delete this estimate? This action cannot be undone."
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteDialog(false);
          setEstimateToDelete(null);
        }}
      />
    </div>
  );
}

export default ManageEstimates;
